import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Button, Box, Paper, Typography, Chip, TextField,
  useMediaQuery, useTheme, Stack, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { Block, CalendarToday } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { API_BASE_URL, REGISTROS_ANULADOS_LISTAR, REGISTROS_SUCURSALES } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const toYmd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (f) => {
  if (!f) return '—';
  const d = new Date(String(f).replace(' ', 'T'));
  if (isNaN(d)) return String(f);
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

function ReporteAnulaciones() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [registros, setRegistros] = useState([]);
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d; });
  const [endDate, setEndDate] = useState(new Date());
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState(''); // '' = todas

  useEffect(() => {
    axios.get(API_BASE_URL + REGISTROS_SUCURSALES, { headers: authHeader() })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setSucursales(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setSucursales([]));
  }, []);

  const fetchRegistros = useCallback(() => {
    setLoading(true);
    axios.get(API_BASE_URL + REGISTROS_ANULADOS_LISTAR, {
      params: { startDate: toYmd(startDate), endDate: toYmd(endDate), sucursal_id: sucursalId || undefined },
      headers: authHeader(),
    })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setRegistros(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setRegistros([]))
      .finally(() => setLoading(false));
  }, [startDate, endDate, sucursalId]);

  useEffect(() => { fetchRegistros(); }, [fetchRegistros]);

  const totalPeriodo = registros.reduce((a, r) => a + Number(r.total || 0), 0);

  const columns = [
    { field: 'fecha', headerName: 'Fecha venta', width: 150, valueGetter: (p) => fmtFecha(p.row.fecha) },
    { field: 'sucursal', headerName: 'Local', flex: 1, minWidth: 130 },
    { field: 'vendedor', headerName: 'Vendedor', flex: 1, minWidth: 130 },
    { field: 'items', headerName: 'Ítems', width: 80, type: 'number', align: 'center', headerAlign: 'center' },
    {
      field: 'total', headerName: 'Total', width: 130, align: 'right', headerAlign: 'right',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoneda(p.row.total)}</Typography>,
    },
    { field: 'anulado_por', headerName: 'Anuló', width: 130, valueGetter: (p) => p.row.anulado_por || '—' },
    { field: 'anulado_fecha', headerName: 'Fecha anulación', width: 150, valueGetter: (p) => fmtFecha(p.row.anulado_fecha) },
  ];

  const filtros = (
    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      {isMobile ? (
        <>
          <DatePicker selected={startDate} onChange={setStartDate} dateFormat="dd/MM/yyyy"
            customInput={<TextField size="small" fullWidth InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} /> }} />} />
          <DatePicker selected={endDate} onChange={setEndDate} dateFormat="dd/MM/yyyy"
            customInput={<TextField size="small" fullWidth InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} /> }} />} />
        </>
      ) : (
        <>
          <DatePicker selected={startDate} onChange={setStartDate} selectsStart startDate={startDate} endDate={endDate}
            dateFormat="dd/MM/yyyy" customInput={<TextField label="Fecha Desde" size="small" />} />
          <DatePicker selected={endDate} onChange={setEndDate} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
            dateFormat="dd/MM/yyyy" customInput={<TextField label="Fecha Hasta" size="small" />} />
        </>
      )}
      {sucursales.length > 0 && (
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select label="Sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {sucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      <Button onClick={fetchRegistros} variant="contained">Buscar</Button>
    </Box>
  );

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Block}
          titulo="Reporte de anulaciones"
          subtitulo={`${registros.length} anulación${registros.length !== 1 ? 'es' : ''} en el período`}
          ayuda="Ventas anuladas en el período elegido, con quién las anuló y cuándo."
        />

        {filtros}

        {!loading && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Paper variant="outlined" sx={{ px: 2, py: 1, flex: '1 1 auto', minWidth: 120 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{registros.length}</Typography>
              <Typography variant="caption" color="text.secondary">Anulaciones</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ px: 2, py: 1, flex: '1 1 auto', minWidth: 120 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'primary.main' }}>{fmtMoneda(totalPeriodo)}</Typography>
              <Typography variant="caption" color="text.secondary">Total anulado</Typography>
            </Paper>
          </Box>
        )}

        {loading ? (
          <Cargando />
        ) : isMobile ? (
          <Stack spacing={1.5}>
            {registros.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Sin anulaciones en el período.
              </Typography>
            )}
            {registros.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 1.5, borderLeft: '4px solid', borderLeftColor: 'error.main' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{fmtMoneda(r.total)}</Typography>
                  <Chip size="small" label={`${r.items} ítems`} variant="outlined" />
                </Box>
                <Typography variant="body2" sx={{ mt: 0.25 }}>{r.sucursal || '—'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Venta: {fmtFecha(r.fecha)} · {r.vendedor || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Anuló: {r.anulado_por || '—'} · {fmtFecha(r.anulado_fecha)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <div style={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={registros} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
              getRowId={(row) => row.id} disableSelectionOnClick
              components={{ Toolbar: ExportToolbar }}
            />
          </div>
        )}
      </Box>
    </Container>
  );
}

export default ReporteAnulaciones;
